import React, { useState, useEffect } from "react";
import { Button, Input, List, message } from "antd";
import dayjs from "dayjs";
import { MagnifyingGlassIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { useTranslation } from 'react-i18next';
import axiosInstance from "../axios/axiosInstance";
import { ActionButton, DataTable, PageCard, PageShell, PageToolbar } from "../components/ui";

const PatientSmsPage = () => {
    const { t } = useTranslation();
    const [patients, setPatients] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedPatients, setSelectedPatients] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [loadingPatients, setLoadingPatients] = useState(false);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [showBirthdayOnly, setShowBirthdayOnly] = useState(false);

    // Fetch patients
    const fetchPatients = async () => {
        setLoadingPatients(true);
        try {
            const response = await axiosInstance.get("/patient/all");
            setPatients(response.data.data);
        } catch (error) {
            message.error(t('sms.loadPatientsError'));
        } finally {
            setLoadingPatients(false);
        }
    };

    // Fetch templates
    const fetchTemplates = async () => {
        setLoadingTemplates(true);
        try {
            const response = await axiosInstance.get("/sample/all");
            setTemplates(response.data.data);
        } catch (error) {
            message.error(t('sms.loadTemplatesError'));
        } finally {
            setLoadingTemplates(false);
        }
    };

    useEffect(() => {
        fetchPatients();
        fetchTemplates();
    }, []);

    const today = dayjs();

    const filteredPatients = patients.filter((patient) => {
      const matchesSearch = patient.name.toLowerCase().includes(searchTerm.toLowerCase());
    
      if (!showBirthdayOnly) return matchesSearch;
    
      const birthday = dayjs(patient.birth_at);
      const isBirthdayToday = birthday.date() === today.date() && birthday.month() === today.month();
    
      return matchesSearch && isBirthdayToday;
    });

    

    // Handle sending SMS
    const handleSendSms = async () => {
        if (!selectedPatients.length || !selectedTemplate) {
            message.warning(t('sms.selectWarning'));
            return;
        }

        const payload = {
            data: selectedPatients.map((patient) => ({
                patient_id: patient.id,
                phone: patient.phone,
            })),
            sample_sms_id: selectedTemplate.id,
        };

        try {
            await axiosInstance.post("/patient/send/sms", payload);
            message.success(t('sms.sent'));
            setSelectedPatients([]);
            setSelectedTemplate(null);
        } catch (error) {
            message.error(t('sms.sendError'));
        }
    };

    const patientColumns = [
        {
            title: t('patients.colId'),
            dataIndex: "index",
            key: "index",
            width: 64,
            render: (_, __, index) => <span className="text-xs text-slate-500 tabular-nums">{index + 1}</span>,
        },
        {
            title: t('patients.colName'),
            dataIndex: "name",
            key: "name",
            render: (name) => <span className="text-sm font-medium text-[#1D2C7B]">{name}</span>,
        },
        {
            title: t('patients.colBirth'),
            dataIndex: "birth_at",
            key: "birth_at",
            width: 140,
            render: (date) => (
                <span className="text-sm text-slate-700 tabular-nums">{dayjs(date).format("DD.MM.YYYY")}</span>
            ),
          },
        {
            title: t('patients.colPhone'),
            dataIndex: "phone",
            key: "phone",
            width: 160,
            render: (phone) => (
                <a href={`tel:+${phone}`} className="text-sm text-[#1D2C7B] tabular-nums whitespace-nowrap">
                    {phone}
                </a>
            ),
        },
    ];

    const rowSelection = {
        onChange: (_, selectedRows) => {
            setSelectedPatients(selectedRows);
        },
    };

    const renderPatientMobileCard = (record) => (
        <div>
            <p className="m-0 text-sm font-semibold text-[#1D2C7B] break-words">{record.name}</p>

            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
                <span className="tabular-nums">{dayjs(record.birth_at).format("DD.MM.YYYY")}</span>
                <a href={`tel:+${record.phone}`} className="tabular-nums text-[#1D2C7B]">
                    {record.phone}
                </a>
            </div>
        </div>
    );

    const sendDisabled = !selectedPatients.length || !selectedTemplate;

    return (
        <PageShell>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5 md:gap-4 items-start">
                {/* Left Panel */}
                <PageCard>
                    <PageToolbar
                        title={t('sms.title')}
                        count={filteredPatients.length}
                        filters={
                            <>
                                <Input
                                    size="small"
                                    allowClear
                                    prefix={<MagnifyingGlassIcon className="w-4 h-4 text-slate-400" />}
                                    placeholder={t('sms.searchPatient')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="md:!w-[180px]"
                                />
                                <Button
                                    size="small"
                                    onClick={() => setShowBirthdayOnly((prev) => !prev)}
                                    type={showBirthdayOnly ? "primary" : "default"}
                                    style={
                                        showBirthdayOnly
                                            ? { backgroundColor: "#1D2C7B", borderColor: "#1D2C7B" }
                                            : undefined
                                    }
                                >
                                    {showBirthdayOnly ? t('sms.showAll') : t('sms.birthdayToday')}
                                </Button>
                            </>
                        }
                    />

                    <DataTable
                        dataSource={filteredPatients}
                        columns={patientColumns}
                        rowKey="id"
                        scrollX={720}
                        mobileCard={renderPatientMobileCard}
                        rowSelection={{
                            type: "checkbox",
                            ...rowSelection,
                        }}
                        loading={loadingPatients}
                        pagination={false}
                        className="[&_.ant-table-selection-column]:!pl-2.5 [&_.ant-table-selection-column>*]:!m-0 [&_.ant-table-selection-column>*]:!border-0 [&_.ant-table-selection-column>*]:!bg-transparent [&_.ant-table-selection-column>*]:!p-0"
                    />
                </PageCard>

                {/* Right Panel */}
                <PageCard>
                    <PageToolbar
                        title={t('sms.templates')}
                        actions={
                            <ActionButton
                                icon={<PaperAirplaneIcon className="w-4 h-4" />}
                                onClick={handleSendSms}
                                disabled={sendDisabled}
                                style={sendDisabled ? {} : { backgroundColor: "#1D2C7B", borderColor: "#1D2C7B" }}
                            >
                                {t('sms.send')}
                            </ActionButton>
                        }
                    />

                    <div className="p-2.5 sm:p-3">
                        <List
                            dataSource={templates}
                            renderItem={(template) => (
                                <List.Item
                                    className={`!block w-full cursor-pointer rounded-xl !border !px-3 !py-2.5 mb-2 text-sm transition-colors ${
                                        selectedTemplate?.id === template.id
                                            ? "!border-[#1D2C7B] bg-indigo-50/60 text-[#1D2C7B]"
                                            : "!border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                                    }`}
                                    onClick={() => setSelectedTemplate(template)}
                                >
                                    {template.text}
                                </List.Item>
                            )}
                            loading={loadingTemplates}
                        />
                    </div>
                </PageCard>
            </div>
        </PageShell>
    );
};

export default PatientSmsPage;
